import { TaxiService } from "./records/taxi-service-record";

export enum TypeProduit {
  Transfert = 0,
  MiseADisposition = 1,
  DriverOnly = 2,
}

export enum TypeVehicule {
  Berline = 0,
  Van = 1,
}

export const TypeVehiculeLabel: Record<TypeVehicule, string> = {
  [TypeVehicule.Berline]: "Berline / Break",
  [TypeVehicule.Van]: "Van",
};

// Clé de traduction (enums.type_produit.*) associée à chaque type de produit,
// pour éviter de dupliquer cette correspondance dans chaque page.
export const TypeProduitTranslationKey: Record<TypeProduit, string> = {
  [TypeProduit.Transfert]: "transfert",
  [TypeProduit.MiseADisposition]: "mise_a_disposition",
  [TypeProduit.DriverOnly]: "driver_only",
};

export function buildTypeProduitLabels(
  tEnums: (key: string) => string,
): Record<TypeProduit, string> {
  return Object.fromEntries(
    Object.entries(TypeProduitTranslationKey).map(([type, key]) => [
      type,
      tEnums(`type_produit.${key}`),
    ]),
  ) as Record<TypeProduit, string>;
}

// Clé de traduction (enums.type_vehicule.*) associée à chaque type de véhicule,
// pour éviter de dupliquer cette correspondance dans chaque page.
export const TypeVehiculeTranslationKey: Record<TypeVehicule, string> = {
  [TypeVehicule.Berline]: "berline",
  [TypeVehicule.Van]: "van",
};

export function buildTypeVehiculeLabels(
  tEnums: (key: string) => string,
): Record<TypeVehicule, string> {
  return Object.fromEntries(
    Object.entries(TypeVehiculeTranslationKey).map(([type, key]) => [
      type,
      tEnums(`type_vehicule.${key}`),
    ]),
  ) as Record<TypeVehicule, string>;
}

export type CourseEstimateRequest = {
  idService: number;
  typeProduit: TypeProduit;
  typeVehicule?: TypeVehicule | null;
  nombresPassagers: number;
  dateCourse: string;
  dateTrajetRetour?: string | null;
  avecTrajetRetour: boolean;
  dureeMinutes?: number | null;
  arrets: {
    ordre: number;
    type: number;
    adresse: {
      rue: string;
      numero: string;
      boite: string;
      codePostal: string;
      ville: string;
      pays: string;
      // Présent uniquement si l'adresse vient d'une suggestion Google. Optionnel :
      // absent ⇒ le backend retombe sur l'adresse texte (aucune régression).
      placeId?: string;
    };
  }[];
};

export type CourseEstimateResult = {
  /** Montant total, TVA comprise. */
  prixTotal: number;
  /** Part hors TVA du montant total. */
  prixTotalHtva: number;
  /** Part de TVA du montant total. */
  prixTotalTva: number;
  prixBase: number;
  distanceKm: number;
  typeVehicule: number | null;
  trancheAppliquee: string;
  heurePointe: boolean;
  majorationHeurePointe: number;
  supplementHeurePointe: number;
  horsBruxelles: boolean;
  supplementHorsBruxelles: number;
  prixTrajetRetour: number;
  tauxTva: number;
};

/**
 * Attribution automatique du type de véhicule, alignée sur la règle backend :
 * Van au-delà de 4 passagers, Berline sinon.
 */
export const deduceTypeVehicule = (
  _idService: number,
  nombresPassagers: number,
): TypeVehicule =>
  nombresPassagers > 4 || _idService === TaxiService.MiniVan
    ? TypeVehicule.Van
    : TypeVehicule.Berline;

export const formatPrice = (value: number): string =>
  new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
  }).format(value ?? 0);
